import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import {
  radiusHelper,
  paletteHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { useMediaGalleryModalContext } from '../../../../contexts/MediaGalleryModalContext/MediaGalleryModalContext';
import timelineImagesEdgeSvg from '../../../../../assets/timeline-images-edge.svg';
import {
  MediaCount,
  PropertyTimelineMediaCounts,
} from '../PropertyTimelineMediaCounts/PropertyTimelineMediaCounts';

export interface PropertyTimelineMediaProps {
  image: {
    alt: string;
    jpgUri: string;
    webpUri: string;
  };
  counts: MediaCount[];
  caption?: string | null;
  galleryItemKey?: string;
}

interface ImageWithEdgeProps {
  image: {
    alt: string;
    jpgUri: string;
    webpUri: string;
  };
}

const StyledImage = styled.img`
  border-radius: ${radiusHelper('medium')};
  height: 56px;
  width: 88px;
`;

const ImageAndMediaCountsButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;

  &:hover {
    cursor: pointer;
    ${StyledImage} {
      transform: scale(1.1);
      transition: transform 0.2s;
    }

    color: ${paletteHelper('textPrimary')};
  }
`;

const ImageWithEdgeWrapper = styled.div`
  height: 56px;
  width: 100px;
  margin-right: ${spacingHelper('medium')};
  display: flex;
  flex-direction: row;
`;

const ImageWithEdge: FunctionComponent<ImageWithEdgeProps> = ({
  image: { alt, jpgUri, webpUri },
}) => (
  <ImageWithEdgeWrapper>
    <picture>
      <source srcSet={webpUri} type="image/webp" />
      <StyledImage alt={alt} src={jpgUri} />
    </picture>
    <img alt="" src={timelineImagesEdgeSvg} />
  </ImageWithEdgeWrapper>
);

const CaptionText = styled.div`
  ${typographyHelper('body02')};
  color: ${paletteHelper('textSecondary')};
  margin-top: ${spacingHelper('small')};
`;

export const PropertyTimelineMedia: FunctionComponent<PropertyTimelineMediaProps> = ({
  galleryItemKey,
  image,
  counts,
  caption,
}) => {
  const { showModal } = useMediaGalleryModalContext();
  return (
    <>
      <ImageAndMediaCountsButton
        type="button"
        onClick={() => {
          showModal({
            openAction: 'TimelineMedia:Click',
            galleryItemKey,
          });
        }}
        aria-label="Open Media Gallery Lightbox"
      >
        <ImageWithEdge image={image} />
        <PropertyTimelineMediaCounts counts={counts} />
      </ImageAndMediaCountsButton>
      {caption && <CaptionText>{caption}</CaptionText>}
    </>
  );
};
